import { Controller } from "@hotwired/stimulus"
import "@selectize/selectize"

export default class extends Controller {
  static get targets() {
    return [
      "select",
      "selectedOption",
      "list",
      "tagTemplate",
      "selectedOptionTemplate",
      "submitButton",
      "button",
      "selectedOptions",
      "selectedManyOptions",
      "selectedManyOptionsValue",
      "option",
      "containsInputWrapper",
      "selectedIsPresent",
      "selectedIsEmpty",
    ]
  }

  static get values() {
    return {
      proposals: { type: Array, default: [] },
      selected: { type: Array, default: [] },
      name: String,
      showAggregate: { type: Boolean, default: true },
      context: { type: String, default: "" },
    }
  }

  connect() {
    this.draftValues = []
    this.currentSelectedValue = []
    this.filterValue = ""
    this._updatingSelectedValue = false

    this.#syncFilterValueFromSelected()

    if (this.hasButtonTarget) {
      this.buttonTarget.addEventListener("show.bs.dropdown", () => {
        this.#render()
      })
    }

    if (this.hasSelectTarget) {
      this.#initializeSelectize()
    }

    if (this.currentSelectedValue.length > 0) {
      this.draftValues = [...this.currentSelectedValue]
    }

    this.#render()
  }

  #syncFilterValueFromSelected() {
    const values = Array.isArray(this.selectedValue)
      ? this.selectedValue
      : this.selectedValue
        ? [this.selectedValue]
        : []

    this.#setFilterStateFromValues(values)
  }

  #setFilterStateFromValues(values) {
    if (values.length === 0) {
      this.filterValue = ""
      this.currentSelectedValue = []
    } else if (values.includes("*")) {
      this.filterValue = "*"
      this.currentSelectedValue = []
    } else if (values.includes("~")) {
      this.filterValue = "~"
      this.currentSelectedValue = []
    } else {
      this.filterValue = "contains"
      this.currentSelectedValue = [...values]
    }
  }

  #isSpecialValue() {
    return this.filterValue === "*" || this.filterValue === "~"
  }

  #updateSelectedValueFromState() {
    this._updatingSelectedValue = true
    if (this.filterValue === "*") {
      this.selectedValue = ["*"]
    } else if (this.filterValue === "~") {
      this.selectedValue = ["~"]
    } else if (this.filterValue === "contains") {
      this.selectedValue = [...this.currentSelectedValue]
    } else {
      this.selectedValue = []
    }
    this._updatingSelectedValue = false
  }

  apply() {
    const selectedOption = this.optionTargets.find((opt) => opt.checked)
    let values = []

    if (selectedOption?.value === "*") {
      values = ["*"]
    } else if (selectedOption?.value === "~") {
      values = ["~"]
    } else {
      values = [...this.draftValues]
    }

    this.#setFilterStateFromValues(values)
    this.#updateSelectedValueFromState()
    this.draftValues = []
    this.submitButtonTarget.disabled = true
    this.#render()
    this.#dispatchFilterEvent()

    if (this.hasButtonTarget) {
      const dropdown = bootstrap.Dropdown.getInstance(this.buttonTarget)
      dropdown.hide()
    }
  }

  clear() {
    const hadChanges = this.filterValue !== "" || this.currentSelectedValue.length > 0 || this.draftValues.length > 0

    this.#setFilterStateFromValues([])
    this.#updateSelectedValueFromState()
    this.draftValues = []

    this.optionTargets.forEach((option) => {
      option.checked = false
    })

    if (this.hasContainsInputWrapperTarget) {
      this.containsInputWrapperTarget.classList.add("d-none")
    }

    if (this.hasSelectTarget && $(this.selectTarget).data().selectize) {
      $(this.selectTarget).data().selectize.clear()
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }

    this.#render()

    if (hadChanges) {
      this.#dispatchFilterEvent()
    }
  }

  remove(event) {
    const optionAsString = event.params.option.toString()
    const isAppliedFilter = this.currentSelectedValue.includes(optionAsString)

    this.draftValues = this.draftValues.filter((value) => value !== optionAsString)
    this.currentSelectedValue = this.currentSelectedValue.filter((value) => value !== optionAsString)
    this.#updateSelectedValueFromState()

    if (this.hasSelectTarget && $(this.selectTarget).data().selectize) {
      $(this.selectTarget).data().selectize.removeItem(optionAsString)
    }

    this.#render()

    if (isAppliedFilter) {
      this.#dispatchFilterEvent()
    }
  }

  #dispatchFilterEvent() {
    const values = this.#isSpecialValue() ? [this.filterValue] : this.currentSelectedValue

    const detail = {
      name: this.nameValue,
      values,
    }

    // Only add context if it's explicitly set (backwards compatibility)
    if (this.contextValue) {
      detail.context = this.contextValue
    }

    this.dispatch("filter", { detail })
  }

  #render() {
    const userCheckedOption = this.optionTargets.find((opt) => opt.checked)
    const isUserSelectingValues = this.draftValues && this.draftValues.length > 0

    if (!userCheckedOption && !isUserSelectingValues) {
      this.#syncFilterValueFromSelected()
    }

    this.#renderRadioButtons(userCheckedOption)
    this.#renderContainsWrapper()
    this.#renderTags()
    this.#updateButtonFilterCount()
    this.showSelectedFilters()
  }

  #renderRadioButtons(userCheckedOption) {
    this.optionTargets.forEach((option) => {
      if (userCheckedOption === option) {
        option.checked = true
        if (option.value === "" && this.hasContainsInputWrapperTarget) {
          this.containsInputWrapperTarget.classList.remove("d-none")
        }
      } else if (!userCheckedOption) {
        if (option.value === "*" || option.value === "~") {
          option.checked = option.value === this.filterValue
        } else if (option.value === "") {
          option.checked = this.#isContainsMode()
          if (option.checked && this.hasContainsInputWrapperTarget) {
            this.containsInputWrapperTarget.classList.remove("d-none")
          }
        } else {
          option.checked = false
        }
      } else {
        option.checked = false
      }
    })
  }

  #isContainsMode() {
    return (
      this.filterValue === "contains" ||
      (!this.#isSpecialValue() && (this.currentSelectedValue.length > 0 || this.draftValues.length > 0))
    )
  }

  #getContainsCheckedOption() {
    return this.optionTargets.find((opt) => opt.value === "" && opt.checked)
  }

  #renderContainsWrapper() {
    const containsChecked = this.#getContainsCheckedOption()
    if (!containsChecked && this.hasContainsInputWrapperTarget) {
      this.containsInputWrapperTarget.classList.add("d-none")
    }
  }

  #renderTags() {
    if (!this.hasListTarget) return

    this.listTarget.innerHTML = ""

    const containsChecked = this.#getContainsCheckedOption()
    const isContainsSelected =
      this.#isContainsMode() || containsChecked || (this.draftValues && this.draftValues.length > 0)

    if (!isContainsSelected) return

    this.currentSelectedValue.forEach((option) => {
      this.#renderDropdownItem(option)
    })

    if (this.draftValues && Array.isArray(this.draftValues)) {
      this.draftValues.forEach((option) => {
        if (!this.currentSelectedValue.includes(option)) {
          this.#renderDropdownItem(option)
        }
      })
    }
  }

  #updateButtonFilterCount() {
    if (this.hasButtonTarget) {
      const filterCount = this.#isSpecialValue() ? 1 : this.currentSelectedValue.length
      this.buttonTarget.dataset.selectedFilters = filterCount
    }
  }

  #renderDropdownItem(option) {
    const clone = this.tagTemplateTarget.content.cloneNode(true)
    const element = clone.firstElementChild
    element.innerHTML = element.innerHTML.replaceAll("$VALUE", option)
    this.listTarget.append(element)
  }

  showSelectedFilters() {
    if (this.hasSelectedIsPresentTarget) {
      this.selectedIsPresentTarget.classList.add("d-none")
    }
    if (this.hasSelectedIsEmptyTarget) {
      this.selectedIsEmptyTarget.classList.add("d-none")
    }
    if (this.hasSelectedManyOptionsTarget) {
      this.selectedManyOptionsTarget.classList.add("d-none")
    }
    this.selectedOptionTargets.forEach((selectedOption) => {
      selectedOption.classList.add("d-none")
    })

    if (this.filterValue === "*") {
      if (this.hasSelectedIsPresentTarget) {
        this.selectedIsPresentTarget.classList.remove("d-none")
      }
      return
    }

    if (this.filterValue === "~") {
      if (this.hasSelectedIsEmptyTarget) {
        this.selectedIsEmptyTarget.classList.remove("d-none")
      }
      return
    }

    if (this.showAggregateValue && this.currentSelectedValue.length > 2) {
      if (this.hasSelectedManyOptionsTarget) {
        this.selectedManyOptionsTarget.classList.remove("d-none")
      }

      if (this.hasSelectedManyOptionsValueTarget) {
        this.selectedManyOptionsValueTarget.innerText = this.currentSelectedValue.length
      }

      this.selectedOptionTargets.forEach((selectedOption) => {
        selectedOption.classList.add("d-none")
      })
      return
    }

    if (this.hasSelectedManyOptionsTarget) {
      this.selectedManyOptionsTarget.classList.add("d-none")
    }

    // Create selectedOption elements for values that don't have them yet
    this.currentSelectedValue.forEach((value) => {
      const existingOption = this.selectedOptionTargets.find((option) => option.dataset.value === value)

      if (!existingOption && this.hasSelectedOptionTemplateTarget) {
        const clone = this.selectedOptionTemplateTarget.content.cloneNode(true)
        const element = clone.firstElementChild

        // Replace $VALUE placeholders in innerHTML (this handles all text content and attributes)
        element.innerHTML = element.innerHTML.replaceAll("$VALUE", value)
        element.dataset.value = value
        this.element.append(element)
      }
    })

    this.selectedOptionTargets.forEach((selectedOption) => {
      if (this.currentSelectedValue.includes(selectedOption.dataset.value)) {
        selectedOption.classList.remove("d-none")
      } else {
        selectedOption.classList.add("d-none")
      }
    })
  }

  get hasFilterValue() {
    const hasJobTitles =
      (this.currentSelectedValue && this.currentSelectedValue.length > 0) ||
      (this.draftValues && this.draftValues.length > 0)
    return this.filterValue !== "" || hasJobTitles
  }

  optionChanged() {
    const checkedOption = this.optionTargets.find((option) => option.checked)
    if (!checkedOption) return

    if (checkedOption.value === "*" || checkedOption.value === "~") {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
      }
      if (this.hasContainsInputWrapperTarget) {
        this.containsInputWrapperTarget.classList.add("d-none")
      }
    } else if (checkedOption.value === "") {
      if (this.hasContainsInputWrapperTarget) {
        this.containsInputWrapperTarget.classList.remove("d-none")
      }
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = this.draftValues.length === 0 && this.currentSelectedValue.length === 0
      }
    }
  }

  #initializeSelectize() {
    const self = this
    const selectizeInstance = $(this.selectTarget).selectize({
      create: true,
      openOnFocus: false,
      setFirstOptionActive: true,
      onFocus: function () {
        this.close()
      },
      onType: function (str) {
        if (!str.length) {
          this.close()
          return
        }
        this.open()
      },
      render: {
        option_create: function (data, escape) {
          return '<div class="create">' + escape(data.input) + "</div>"
        },
        item: function () {
          return '<div style="display: none"></div>'
        },
      },
      onChange: function (value) {
        self.draftValues = value
        self.submitButtonTarget.disabled = false
        self.#render()
        this.close()
      },
      onDropdownOpen: function () {
        if (this.getTextboxValue().length === 0) {
          this.close()
        }
      },
    })[0].selectize

    if (this.currentSelectedValue.length > 0) {
      selectizeInstance.setValue(this.currentSelectedValue)
    }
  }
}
